<template>
    <div class="bg-white rounded shadow">
        <header class="flex items-center px-4 py-3 border-b sm:px-6 md:py-4">
            <div class="flex-grow">
                <h1 class="text-lg font-normal">
                    <truncate-hack>
                        <template v-if="listing">
                            Edit {{ listing.property.name }} listing
                        </template>
                        <template v-else>
                            Create a listing
                        </template>
                    </truncate-hack>
                </h1>
            </div>

            <div class="w-6 h-6">
                <a
                    href="https://help.rentora.co.uk/article/41-how-do-i-create-a-listing"
                    target="_blank"
                    rel="noopener"
                >
                    <base-svg
                        name="icon-help-circle"
                        class="w-full h-full"
                    />
                </a>
            </div>
        </header>

        <base-form @submit="$emit('submit', form)">
            <div class="px-4 pt-6 border-b sm:px-6">
                <validator-alert :sid="sid" />

                <form-section>
                    <template slot="title">Property</template>
                    <template slot="description">
                        Which property are you listing?
                    </template>

                    <grid-layout>
                        <grid-layout-item v-if="createProperty">
                            <form-items-address
                                v-model="form.property.address"
                                :sid="sid"
                                :disabled="is_saving"
                                validator-name="property.address"
                            />
                        </grid-layout-item>

                        <grid-layout-item
                            v-else
                            class="md:w-1/2"
                        >
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="property"
                            >
                                <template slot="label">Property</template>

                                <input-property
                                    v-model="form.property"
                                    :uuid="_.get(listing, 'property.uuid', $route.query.property)"
                                    :sid="sid"
                                    :disabled="is_saving"
                                    name="property"
                                />

                                <template slot="hint">
                                    Do you need to
                                    <router-link
                                        :to="{
                                            name: $router.fix('dashboard.*.properties.create'),
                                            query: {
                                                then: 'create-listing'
                                            }
                                        }"
                                        class="text-blue"
                                    >
                                        create a property
                                    </router-link>
                                    first?
                                </template>
                            </form-item>
                        </grid-layout-item>

                        <grid-layout-item class="md:w-1/2">
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="property_type"
                            >
                                <template slot="label">Type</template>

                                <input-select
                                    v-model="form.property_type"
                                    :sid="sid"
                                    :disabled="is_saving"
                                    placeholder="Select property type"
                                    :options="[
                                        { value: 'detached', label: 'Detatched' },
                                        { value: 'semi-detached', label: 'Semi-Detatched' },
                                        { value: 'terraced', label: 'Terraced' },
                                        { value: 'flat', label: 'Flat' },
                                        { value: 'apartment', label: 'Apartment' },
                                        { value: 'bungalow', label: 'Bungalow' },
                                        { value: 'land', label: 'Land' },
                                        { value: 'commercial', label: 'Commercial Property' },
                                        { value: 'other', label: 'Other' },
                                    ]"
                                    name="property_type"
                                />
                            </form-item>
                        </grid-layout-item>
                    </grid-layout>
                </form-section>
            </div>

            <div class="px-4 pt-6 border-b sm:px-6">
                <form-section>
                    <template slot="title">Rooms &amp; Features</template>
                    <template slot="description">
                        Which property are you listing, and the details of it.
                    </template>

                    <grid-layout>
                        <grid-layout-item class="md:w-1/2">
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="number_of_bedrooms"
                            >
                                <template slot="label">No. of Bedrooms</template>

                                <input-select
                                    v-model="form.number_of_bedrooms"
                                    :sid="sid"
                                    :disabled="is_saving"
                                    placeholder="Select no. of bedrooms"
                                    :options="[
                                        { value: '0', label: '0' },
                                        { value: '1', label: '1' },
                                        { value: '2', label: '2' },
                                        { value: '3', label: '3' },
                                        { value: '4', label: '4' },
                                        { value: '5', label: '5' },
                                        { value: '6', label: '6' },
                                        { value: '7', label: '7+' },
                                    ]"
                                    name="number_of_bedrooms"
                                />
                            </form-item>
                        </grid-layout-item>

                        <grid-layout-item class="md:w-1/2">
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="number_of_bathrooms"
                            >
                                <template slot="label">No. of Bathrooms</template>

                                <input-select
                                    v-model="form.number_of_bathrooms"
                                    :sid="sid"
                                    :disabled="is_saving"
                                    placeholder="Select no. of bathrooms"
                                    :options="[
                                        { value: '0', label: '0' },
                                        { value: '1', label: '1' },
                                        { value: '2', label: '2' },
                                        { value: '3', label: '3' },
                                        { value: '4', label: '4' },
                                        { value: '5', label: '5' },
                                        { value: '6', label: '6' },
                                        { value: '7', label: '7+' },
                                    ]"
                                    name="number_of_bathrooms"
                                />
                            </form-item>
                        </grid-layout-item>

                        <grid-layout-item class="md:w-1/2">
                            <form-item
                                    :sid="sid"
                                    :is-required="true"
                                    name="number_of_receptions"
                            >
                                <template slot="label">No. of Receptions</template>

                                <input-select
                                    v-model="form.number_of_receptions"
                                    :sid="sid"
                                    :disabled="is_saving"
                                    placeholder="Select no. of receptions"
                                    :options="[
                                        { value: '0', label: '0' },
                                        { value: '1', label: '1' },
                                        { value: '2', label: '2' },
                                        { value: '3', label: '3' },
                                        { value: '4', label: '4' },
                                        { value: '5', label: '5' },
                                        { value: '6', label: '6' },
                                        { value: '7', label: '7+' },
                                    ]"
                                    name="number_of_receptions"
                                />

                                <template slot="help">
                                    A room suitable for entertaining visitors, where people can sit, for example a living room.
                                    This room is not usually used for cooking, sleeping, etc.
                                </template>
                            </form-item>
                        </grid-layout-item>

                        <grid-layout-item>
                            <form-item
                                :sid="sid"
                                name="_"
                            >
                                <template slot="label">Features & Allowances</template>

                                <grid-layout>
                                    <grid-layout-item class="w-1/2 lg:w-1/3">
                                        <form-item
                                            :sid="sid"
                                            name="is_furnished"
                                        >
                                            <label class="flex items-center cursor-pointer">
                                                <input-checkbox
                                                    :sid="sid"
                                                    v-model="form.is_furnished"
                                                    name="is_furnished"
                                                    class="mr-3"
                                                />

                                                <div class="leading-none">Furnished</div>
                                            </label>
                                        </form-item>
                                    </grid-layout-item>

                                    <grid-layout-item class="w-1/2 lg:w-1/3">
                                        <form-item
                                            :sid="sid"
                                            name="has_parking"
                                        >
                                            <label class="flex items-center cursor-pointer">
                                                <input-checkbox
                                                    :sid="sid"
                                                    v-model="form.has_parking"
                                                    name="has_parking"
                                                    class="mr-3"
                                                />

                                                <div class="leading-none">Parking</div>
                                            </label>
                                        </form-item>
                                    </grid-layout-item>

                                    <grid-layout-item class="w-1/2 lg:w-1/3">
                                        <form-item
                                            :sid="sid"
                                            name="has_garden"
                                        >
                                            <label class="flex items-center cursor-pointer">
                                                <input-checkbox
                                                    :sid="sid"
                                                    v-model="form.has_garden"
                                                    name="has_garden"
                                                    class="mr-3"
                                                />

                                                <div class="leading-none">Garden</div>
                                            </label>
                                        </form-item>
                                    </grid-layout-item>

                                    <grid-layout-item class="w-1/2 lg:w-1/3">
                                        <form-item
                                            :sid="sid"
                                            name="allows_pets"
                                            class="mb-0!"
                                        >
                                            <label class="flex items-center cursor-pointer">
                                                <input-checkbox
                                                    :sid="sid"
                                                    v-model="form.allows_pets"
                                                    name="allows_pets"
                                                    class="mr-3"
                                                />

                                                <div class="leading-none">Pets</div>
                                            </label>
                                        </form-item>
                                    </grid-layout-item>

                                    <grid-layout-item class="w-1/2 lg:w-1/3">
                                        <form-item
                                            :sid="sid"
                                            name="allows_dss"
                                            class="mb-0!"
                                        >
                                            <label class="flex items-center cursor-pointer">
                                                <input-checkbox
                                                    :sid="sid"
                                                    v-model="form.allows_dss"
                                                    name="allows_dss"
                                                    class="mr-3"
                                                />

                                                <div class="leading-none">DSS</div>
                                            </label>
                                        </form-item>
                                    </grid-layout-item>

                                    <grid-layout-item class="w-1/2 lg:w-1/3">
                                        <form-item
                                            :sid="sid"
                                            name="allows_smoking"
                                            class="mb-0!"
                                        >
                                            <label class="flex items-center cursor-pointer">
                                                <input-checkbox
                                                    :sid="sid"
                                                    v-model="form.allows_smoking"
                                                    name="allows_smoking"
                                                    class="mr-3"
                                                />

                                                <div class="leading-none">Smoking</div>
                                            </label>
                                        </form-item>
                                    </grid-layout-item>
                                </grid-layout>
                            </form-item>
                        </grid-layout-item>
                    </grid-layout>
                </form-section>
            </div>

            <div class="px-4 pt-6 border-b sm:px-6">
                <form-section>
                    <template slot="title">Description &amp; Media</template>
                    <template slot="description">
                        Add photos and optionally a video to your listing.
                    </template>

                    <grid-layout>
                        <grid-layout-item>
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="description"
                            >
                                <template slot="label">Description</template>

                                <div class="relative">
                                    <input-textarea
                                        v-model="form.description"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        name="description"
                                        placeholder="e.g. Cozy bedroom on main floor of classic 1920's bungalow next to guest bathroom shared kitchen. Although the room is small, the queen bed is very comfortable, includes a desk, high speed Internet, and a luggage rack."
                                        class="h-64"
                                    />
                                </div>
                            </form-item>
                        </grid-layout-item>

                        <grid-layout-item>
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="uploads"
                            >
                                <template slot="label">Photos</template>

                                <input-uploads
                                    v-model="form.uploads"
                                    name="uploads"
                                    :max-files="20"
                                    :show-thumbs="true"
                                    :sid="sid"
                                    :accepted-files="[
                                        'image/jpeg',
                                        'image/png'
                                    ]"
                                    class="mr-3"
                                />

                                <template slot="hint">At least 1 photo is required. The 1st photo will be the cover image. Drag &amp; drop to reorder.</template>
                            </form-item>
                        </grid-layout-item>

                        <grid-layout-item>
                            <form-item
                                :sid="sid"
                                name="video_url"
                            >
                                <template slot="label">Video Url</template>

                                <input-text
                                    v-model="form.video_url"
                                    :sid="sid"
                                    :disabled="is_saving"
                                    type="url"
                                    name="video_url"
                                    placeholder="https://youtu.be/EvBIfeyTCNk"
                                />

                                <template slot="hint">YouTube videos only.</template>

                                <template slot="help">
                                    Add a video tour to your listing. Get the URL by uploading/finding a video on YouTube, click 'Share' and copy the URL.
                                </template>
                            </form-item>
                        </grid-layout-item>
                    </grid-layout>
                </form-section>
            </div>

            <div class="px-4 pt-6 border-b sm:px-6">
                <form-section>
                    <template slot="title">Tenancy</template>
                    <template slot="description">
                        How much is the rent and deposit, and when will the property be available?
                    </template>

                    <grid-layout>
                        <grid-layout-item class="md:w-1/2">
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="rent_amount"
                            >
                                <template slot="label">Monthly Rent</template>

                                <div class="relative">
                                    <input-text
                                        v-model="form.rent_amount"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        type="number"
                                        name="rent_amount"
                                        class="pl-7"
                                    />

                                    <div class="absolute inset-y-0 left-0 flex items-center px-3 pt-1 margin-px text-grey-darker">
                                        &pound;
                                    </div>
                                </div>
                            </form-item>
                        </grid-layout-item>

                        <grid-layout-item class="md:w-1/2">
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="deposit_amount"
                            >
                                <template slot="label">Deposit</template>

                                <div class="relative">
                                    <input-text
                                        v-model="form.deposit_amount"
                                        :sid="sid"
                                        :disabled="is_saving"
                                        type="number"
                                        name="deposit_amount"
                                        class="pl-7"
                                    />

                                    <div class="absolute inset-y-0 left-0 flex items-center px-3 pt-1 margin-px text-grey-darker">
                                        &pound;
                                    </div>
                                </div>
                            </form-item>
                        </grid-layout-item>

                        <grid-layout-item class="md:w-1/2">
                            <form-item
                                :sid="sid"
                                :is-required="true"
                                name="available_at"
                            >
                                <template slot="label">Available at</template>

                                <input-date
                                    v-model="form.available_at"
                                    :sid="sid"
                                    :disabled="is_saving"
                                    name="available_at"
                                />
                            </form-item>
                        </grid-layout-item>
                    </grid-layout>
                </form-section>
            </div>

            <div class="px-4 py-3 text-right rounded-b bg-grey-bg sm:px-6">
                <base-button
                    :is-loading="is_saving"
                    class="w-full btn-blue md:w-auto"
                >
                    {{ listing ? 'Update' : 'Create' }} Listing
                </base-button>
            </div>
        </base-form>
    </div>
</template>

<script>
    import moment from 'moment'
    import FormItemsAddress from '../../../../../components/form-items-address'
    import InputProperty from '../../../../../components/input-property'

    export default {
        components: {
            FormItemsAddress,
            InputProperty
        },
        props: {
            listing: {
                type: Object,
                default: null
            },
            sid: {
                type: String,
                required: true
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            createProperty: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            is_saving () {
                return this.isLoading
            }
        },
        data () {
            return {
                form: {
                    property: this.createProperty
                        ? {
                            address: {
                                street: '',
                                locality: '',
                                village: '',
                                town: '',
                                county: '',
                                postcode: ''
                            }
                        }
                        : null,
                    description: '',
                    property_type: '',
                    number_of_bedrooms: '',
                    number_of_bathrooms: '',
                    number_of_receptions: '',
                    is_active: true,
                    is_furnished: false,
                    allows_pets: false,
                    has_parking: false,
                    allows_dss: false,
                    has_garden: false,
                    allows_smoking: false,
                    rent_amount: '',
                    deposit_amount: '',
                    available_at: moment().format('DD/MM/YYYY'),
                    video_url: '',
                    uploads: []
                }
            }
        },
        created () {
            if (this.listing) {
                this.form.description = this.listing.description
                this.form.property_type = this.listing.property_type
                this.form.number_of_bedrooms = this.listing.number_of_bedrooms.toString()
                this.form.number_of_bathrooms = this.listing.number_of_bathrooms.toString()
                this.form.number_of_receptions = this.listing.number_of_receptions.toString()
                this.form.is_furnished = !!this.listing.is_furnished
                this.form.allows_pets = !!this.listing.allows_pets
                this.form.has_parking = !!this.listing.has_parking
                this.form.allows_dss = !!this.listing.allows_dss
                this.form.has_garden = !!this.listing.has_garden
                this.form.allows_smoking = !!this.listing.allows_smoking
                this.form.rent_amount = this.listing.rent_amount / 100
                this.form.deposit_amount = this.listing.deposit_amount / 100
                this.form.available_at = moment(this.listing.available_at).format('DD/MM/YYYY')
                this.form.video_url = this.listing.video_url
                this.form.uploads = this.listing.files.data
            }
        },
        methods: {
            reset () {
                this.$validator.reset(this.sid)

                Object.assign(this.$data, this.$options.data.apply(this))
            }
        }
    }
</script>
